import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

export interface DeliveryZone {
  postal_code: string;
  min_order: number;
  delivery_fee: number;
  free_threshold: number;
  available: boolean;
  city?: string;
  branch_id?: string;
}

// Branch-scoped zone doc id. Same PLZ can have different fees per branch
// (Sadik's tables overlap ZIPs across branches), so zones are keyed by
// `{branch}__{zip}`. Legacy docs keyed by bare ZIP remain as Hürth's
// historical global set (fallback below).
const zoneDocId = (branchId: string, zip: string) => (branchId ? `${branchId}__${zip}` : zip);
// Branches that historically shared Hürth's global (bare-ZIP) zone set.
const LEGACY_FALLBACK_BRANCHES = ['huerth', 'donvito'];

export interface DeliveryQuote {
  /** True if delivery is allowed to this ZIP right now (zone exists + active + cart meets min). */
  allowed: boolean;
  /** Human-readable reason when `allowed=false`. Localized by caller. */
  reason?: 'no_zone' | 'disabled' | 'below_min';
  /** The zone, if one was found (may still be disabled). */
  zone: DeliveryZone | null;
  /** Delivery fee that applies at the current subtotal (€). */
  fee: number;
  /** Whether the cart already qualifies for free delivery. */
  isFree: boolean;
  /** If below min_order, this is the shortfall in €. */
  amountBelowMin: number;
  /** How much more the customer needs to spend for free delivery (0 if already free). */
  amountToFreeDelivery: number;
}

const zoneCache = new Map<string, { zone: DeliveryZone | null; fetchedAt: number }>();
const CACHE_TTL = 60 * 1000; // 1 min

function normalizeZip(raw: string): string {
  return (raw || '').toString().trim().replace(/\s+/g, '');
}

export async function loadDeliveryZone(postalCode: string, branchId: string = ''): Promise<DeliveryZone | null> {
  const zip = normalizeZip(postalCode);
  if (!zip) return null;
  const bid = (branchId || '').trim();
  const cacheKey = bid ? `${bid}__${zip}` : zip;

  const cached = zoneCache.get(cacheKey);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL) return cached.zone;

  const readDoc = async (id: string): Promise<any | null> => {
    try { const s = await getDoc(doc(db, 'delivery_zones', id)); return s.exists() ? s.data() : null; }
    catch (e) { console.warn('[DELIVERY ZONES] load error:', e); return null; }
  };

  // 1) branch-scoped doc; 2) legacy bare-ZIP fallback for huerth/donvito (or when no branch given).
  let data: any = null;
  if (bid) data = await readDoc(zoneDocId(bid, zip));
  if (!data && (!bid || LEGACY_FALLBACK_BRANCHES.includes(bid))) data = await readDoc(zip);

  if (!data) {
    zoneCache.set(cacheKey, { zone: null, fetchedAt: Date.now() });
    return null;
  }
  const zone: DeliveryZone = {
    postal_code: data.postal_code || zip,
    min_order: Number(data.min_order) || 0,
    delivery_fee: Number(data.delivery_fee) || 0,
    free_threshold: Number(data.free_threshold) || 0,
    available: data.available !== false,
    city: data.city || '',
    branch_id: data.branch_id || bid || undefined,
  };
  zoneCache.set(cacheKey, { zone, fetchedAt: Date.now() });
  return zone;
}

/**
 * Quote delivery for a subtotal against a specific ZIP's rules. Pure function:
 * does not fetch the zone itself — call `loadDeliveryZone` first.
 */
export function quoteDelivery(zone: DeliveryZone | null, subtotal: number): DeliveryQuote {
  if (!zone) {
    return {
      allowed: false, reason: 'no_zone', zone: null,
      fee: 0, isFree: false, amountBelowMin: 0, amountToFreeDelivery: 0,
    };
  }
  if (!zone.available) {
    return {
      allowed: false, reason: 'disabled', zone,
      fee: 0, isFree: false, amountBelowMin: 0, amountToFreeDelivery: 0,
    };
  }
  const below = Math.max(0, zone.min_order - subtotal);
  if (below > 0) {
    return {
      allowed: false, reason: 'below_min', zone,
      fee: zone.delivery_fee, isFree: false,
      amountBelowMin: below,
      amountToFreeDelivery: Math.max(0, zone.free_threshold - subtotal),
    };
  }
  const isFree = zone.free_threshold > 0 && subtotal >= zone.free_threshold;
  return {
    allowed: true, zone,
    fee: isFree ? 0 : zone.delivery_fee,
    isFree,
    amountBelowMin: 0,
    amountToFreeDelivery: isFree ? 0 : Math.max(0, zone.free_threshold - subtotal),
  };
}
